import { FC, ReactElement } from "react";

import { Maybe } from "graphql/jsutils/Maybe";
import { exists, toDefaultBytes, toDefaultInt, toTitleCase } from "../../lib/Util";
import { CompletedStatusColumn } from "../common/CompletedStatusColumn";
import { CancelStatusIcon, DownloadPhaseStatusIcon, StatusIconContainer } from "../common/StatusIcon";
import { ProgressBarActive, ProgressBarWaiting } from "../ui/ProgressBars";

const isNumber = (value?: number | null): value is number => value != null && !isNaN(value);

const fromProgress = (progress?: ProgressProps | null) => {
  // Get estimated and completed counts for rows, bytes and reports.
  let rows_completed: number | null = null;
  let rows_estimated: number | null = null;
  let bytes_completed: number | null = null;
  let bytes_estimated: number | null = null;
  let reports_completed: number | null = null;
  let reports_estimated: number | null = null;
  if (progress && progress.completed) {
    if (isNumber(progress.completed.rows)) {
      rows_completed = progress.completed.rows;
    }
    if (isNumber(progress.completed.bytes)) {
      bytes_completed = progress.completed.bytes;
    }
    if (isNumber(progress.completed.reports)) {
      reports_completed = progress.completed.reports;
    }
  }
  if (progress && progress.estimated) {
    if (isNumber(progress.estimated.rows)) {
      rows_estimated = progress.estimated.rows;
    }
    if (isNumber(progress.estimated.bytes)) {
      bytes_estimated = progress.estimated.bytes;
    }
    // NOTE: guards on `completed.reports` but reads `estimated.reports`. Preserved as-is.
    if (isNumber(progress.completed?.reports)) {
      reports_estimated = progress.estimated.reports ?? null;
    }
  }

  // Prefer complete estimated/completed over partial, and prefer rows over bytes over reports.
  let item_unit = "rows";
  let items_completed = rows_completed;
  let items_estimated = rows_estimated;
  if (exists(rows_completed) && exists(rows_estimated)) {
    // Keep existing assignments for items*
  } else if (exists(bytes_completed) && exists(bytes_estimated)) {
    item_unit = "bytes";
    items_completed = bytes_completed;
    items_estimated = bytes_estimated;
  } else if (exists(reports_completed) && exists(reports_estimated)) {
    item_unit = "reports";
    items_completed = reports_completed;
    items_estimated = reports_estimated;
  } else if (exists(rows_completed) || exists(rows_estimated)) {
    // Keep existing assignments for items*
  } else if (exists(bytes_completed) || exists(bytes_estimated)) {
    item_unit = "bytes";
    items_completed = bytes_completed;
    items_estimated = bytes_estimated;
  } else if (exists(reports_completed) || exists(reports_estimated)) {
    item_unit = "reports";
    items_completed = reports_completed;
    items_estimated = reports_estimated;
  }
  return { item_unit, items_completed, items_estimated };
};

interface IGetBadgeText {
  has_estimated_info: boolean;
  items_completed: null | number;
  items_estimated: null | number;
  phase: string;
  item_unit: string;
  status: string;
}

const getBadgeText: FC<IGetBadgeText> = ({
  has_estimated_info,
  items_completed,
  items_estimated,
  phase,
  item_unit,
  status,
}) => {
  if (has_estimated_info && items_completed !== null && items_estimated !== null) {
    const progress = Math.min(Math.floor((items_completed / items_estimated) * 100), 100);
    const badgeText =
      item_unit === "bytes"
        ? `${phase}${toDefaultBytes(items_completed)} / ${toDefaultBytes(items_estimated)}`
        : `${phase}${toDefaultInt(items_completed)} / ${toDefaultInt(items_estimated)} ${item_unit}`;
    return <ProgressBarActive status={status} progress={progress} badgeText={badgeText} />;
  } else {
    const badgeText =
      item_unit === "bytes"
        ? `${phase}${toDefaultBytes(items_completed || 0)}`
        : `${phase}${toDefaultInt(items_completed || 0)} ${item_unit}`;
    return <ProgressBarWaiting status={status} badgeText={badgeText} />;
  }
};

const ProgressBarWorkingOrWaiting = ({ status, progress }: { status: string; progress?: ProgressProps | null }) => {
  const { item_unit, items_completed, items_estimated } = fromProgress(progress);
  const has_completed_info = items_completed !== null && items_completed >= 0;
  const has_estimated_info = items_estimated !== null && items_estimated > 0;
  if (status === "queued") {
    return <ProgressBarWaiting status={status} badgeText="Queued" />;
  } else if (status === "retrying") {
    const statusIcon = <DownloadPhaseStatusIcon status={status} large={false} noPadding={true} hideTooltip={true} />;
    return <ProgressBarWaiting status={status} badgeText="Retrying" statusIcon={statusIcon} />;
  } else if (has_completed_info) {
    // prettier-ignore
    const phase =
      status === "requested" ? "Waiting on " :
      status === "unpacking" ? "Unpacking " :
        "";
    return getBadgeText({
      has_estimated_info,
      items_completed,
      items_estimated,
      phase,
      item_unit,
      status,
    });
  } else if (status === "requested") {
    return <ProgressBarWaiting status={status} badgeText="Waiting on Source" />;
  } else if (status === "unpacking") {
    return <ProgressBarWaiting status={status} badgeText="Unpacking" />;
  } else {
    return <ProgressBarWaiting status={status} badgeText={toTitleCase(status)} />;
  }
};

const isDownloadInProgress = (status?: string): status is string => {
  return status !== undefined && ["queued", "requested", "downloading", "unpacking", "retrying"].indexOf(status) > -1;
};
export interface ProgressProps {
  completed?: {
    rows?: number | null;
    bytes?: number | null;
    reports?: number | null;
  };
  estimated?: {
    rows?: number | null;
    bytes?: number | null;
    reports?: number | null;
  };
}

export interface IDownloadStatusIcon {
  status?: string;
  rowCount?: Maybe<number>;
  progress?: ProgressProps;
  dirtyRowCount?: Maybe<number>;
  newDirtyRowCount?: Maybe<number>;
  newDirtyRowStatus?: Maybe<string>;
  dirtyRowStatus?: Maybe<string>;
  downloadPartId?: string;
  finished?: Maybe<string>;
  statusColumnWidth?: number;
  onToggleDetailPanel?: () => void;
}

const DownloadStatusIcon: FC<IDownloadStatusIcon> = ({
  status,
  progress,
  rowCount,
  dirtyRowCount,
  newDirtyRowCount,
  dirtyRowStatus = null,
  newDirtyRowStatus = null,
  downloadPartId = null,
  finished = null,
  statusColumnWidth = 0,
  onToggleDetailPanel,
}) => {
  let icon: ReactElement | null = null;
  if (status === "completed" || status === "STOPPED") {
    status = "completed";
    const hasDirtyRows = dirtyRowCount && dirtyRowCount > 0;
    const hasNewDirtyRows = newDirtyRowCount && newDirtyRowCount > 0;
    const allDirtyRows = dirtyRowCount === rowCount && dirtyRowCount && dirtyRowCount > 0;
    const allNewDirtyRows = newDirtyRowCount === rowCount && newDirtyRowCount && newDirtyRowCount > 0;
    if ((hasDirtyRows && allDirtyRows) || (hasNewDirtyRows && allNewDirtyRows)) {
      status = "error";
    } else if ((hasDirtyRows && !allDirtyRows) || (hasNewDirtyRows && !allNewDirtyRows)) {
      status = "warning";
    }
    return (
      <CompletedStatusColumn
        rowCount={rowCount}
        dirtyRowCount={dirtyRowCount}
        dirtyRowStatus={dirtyRowStatus}
        status={status}
        downloadPartId={downloadPartId}
        newDirtyRowCount={newDirtyRowCount}
        newDirtyRowStatus={newDirtyRowStatus}
        finished={finished}
        statusColumnWidth={statusColumnWidth}
        onToggleDetailPanel={onToggleDetailPanel}
      />
    );
  } else if (status === "cancelled") {
    icon = <CancelStatusIcon />;
  } else if (isDownloadInProgress(status)) {
    icon = <ProgressBarWorkingOrWaiting progress={progress} status={status} />;
  } else {
    icon = <DownloadPhaseStatusIcon status={status} large={false} />;
  }

  return (
    <StatusIconContainer data-cy={status} data-testid={status}>
      {icon}
    </StatusIconContainer>
  );
};

export { DownloadStatusIcon, fromProgress };
